import * as React from 'react';
import { WidthProvider, Responsive } from 'react-grid-layout';
import {
    LAYOUT_BREAKPOINTS,
    BREAKPOINT_COLUMNS,
    DASHBOARD_FIXED_WIDTH,
    DASHBOARD_FIXED_WIDTH_LOW_RES_SCALE,
    DASHBOARD_FIXED_WIDTH_MID_RES_SCALE,
} from 'business/constants';

// this is required to display the resizable handlers for widgets when editing the layout!
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
    Breakstyle,
    // TODO: declare these types in a better location
    WidgetT,
} from 'routes/Dashboard/components/WidgetsLayout/helpers';
import { Box, useMediaQuery } from '@mui/material';
import { isWidgetTruncated } from 'helpers/layoutHelpers';
import { PageSeparator } from 'business/definitions';
import WidgetTruncatedWarningIcon from './WidgetTruncatedWarningIcon';
import {
    buildWidgetsFromRGLPositions,
    getPositionConstraintsFromType,
    getWidgetKey,
    widgetChanged,
} from './helper/layoutHelpers';
import { WidgetPositionContextProvider } from '../context/WidgetPositionContext';
import { useCallback, useEffect, useState } from 'react';
import { moveWidgets } from '../provider/backend';
import { useDebounce } from 'hooks/helpers';
import { useParams } from 'react-router-dom';
import useTheme from '@mui/system/useTheme';
import { getContainerFromParams } from 'lib/layout/helper/containerHelper';

/** WidgetsLayout goal is to display widgets, without knowing much about who they are
 * and how their are stored.
 */
const ResponsiveGridLayout = WidthProvider(Responsive);

const RGLStyle = { filter: 'drop-shadow(0 100px 50px #eee)' };

type Props = {
    widgets: WidgetT[];
    Widget: (arg1: { widget: WidgetT }) => React.ReactElement;
    onLayoutChange: (arg1: WidgetT[]) => void;
    editMode: boolean;
    pageSeparators: PageSeparator[];
    userInitialYPosition?: number;
    saveWidgets?: boolean;
    isFullWidth?: boolean;
};

const DEBOUNCE_TIME = 2000;

export const RGLLayoutEngine = ({
    widgets,
    Widget,
    onLayoutChange,
    editMode,
    pageSeparators,
    userInitialYPosition = 0,
    saveWidgets = false,
    isFullWidth = true,
}: Props): React.ReactElement => {
    const [breakpoint, setBreakpoint] = React.useState<Breakstyle>('md');

    const { budgetProposalId } = useParams<{ budgetProposalId?: string }>();
    const container = getContainerFromParams(budgetProposalId);
    const [referenceWidgets, setReferenceWidget] = useState(() => widgets);

    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('xl'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const [transformScale, setTransformScale] = useState(1);

    useEffect(() => {
        if (isFullWidth) {
            setTransformScale(1);
            return;
        }
        if (isSmallScreen) {
            setTransformScale(DASHBOARD_FIXED_WIDTH_LOW_RES_SCALE);
            return;
        }
        if (isMediumScreen) {
            setTransformScale(DASHBOARD_FIXED_WIDTH_MID_RES_SCALE);
            return;
        }
        setTransformScale(1);
    }, [isMediumScreen, isSmallScreen, isFullWidth]);

    const saveWidgetPosition = useCallback(
        (inputWidgets: WidgetT[]) => {
            if (saveWidgets) {
                moveWidgets(
                    container,
                    inputWidgets[0].layoutId,
                    inputWidgets.map((w) => ({
                        key: w.key,
                        position: w.position,
                    })),
                );
            }
        },
        [container, saveWidgets],
    );

    const debounceSaveWidgets = useDebounce(saveWidgetPosition, DEBOUNCE_TIME);

    useEffect(() => {
        if (!editMode && widgetChanged(referenceWidgets, widgets, breakpoint)) {
            setReferenceWidget(widgets);
            debounceSaveWidgets(widgets);
        }
    }, [
        widgets,
        referenceWidgets,
        setReferenceWidget,
        debounceSaveWidgets,
        editMode,
    ]);

    // we want to sort the widgets so that their order in the HTML matches the order of display
    // this is needed for the accessibility of the platform
    const sortByCoordinate = (widget1: WidgetT, widget2: WidgetT) => {
        // The highest widget is first, otherwise, the closest to the left
        const x1 = widget1.position[breakpoint]?.x || 0;
        const y1 = widget1.position[breakpoint]?.y || 0;

        const x2 = widget2.position[breakpoint]?.x || 0;
        const y2 = widget2.position[breakpoint]?.y || 0;

        if (y1 > y2) {
            return 1;
        }
        if (y2 > y1) {
            return -1;
        }

        if (x1 >= x2) {
            return 1;
        }
        return -1;
    };

    widgets.sort(sortByCoordinate);

    const getWidgetDivStyle = (widget: WidgetT): React.CSSProperties => {
        const shouldWarnWidgetTruncated = editMode && isWidgetTruncated(widget);
        const highlightStyle = {
            border: '2px dashed red',
            overflow: 'clip',
        };
        const baseStyle = {
            width: 'inherit',
            height: 'inherit',
            scrollMarginTop: '250px',
        };
        return shouldWarnWidgetTruncated
            ? { ...baseStyle, ...highlightStyle }
            : baseStyle;
    };

    return (
        <ResponsiveGridLayout
            breakpoints={LAYOUT_BREAKPOINTS}
            onBreakpointChange={(newBreakpoint: Breakstyle) =>
                setBreakpoint(newBreakpoint)
            }
            cols={BREAKPOINT_COLUMNS}
            onLayoutChange={(currentPositions, newPositions) => {
                onLayoutChange(
                    buildWidgetsFromRGLPositions(
                        newPositions,
                        widgets,
                        pageSeparators,
                        isFullWidth,
                    ),
                );
            }}
            rowHeight={isFullWidth ? 30 : 40}
            isDraggable={editMode}
            isResizable={editMode}
            verticalCompact
            draggableCancel=".nondrag"
            style={RGLStyle}
            width={isFullWidth ? undefined : DASHBOARD_FIXED_WIDTH}
            useCSSTransforms={!isFullWidth}
            transformScale={transformScale}
        >
            {widgets.map((widget) => {
                const height = widget.position[breakpoint].h;
                const width = widget.position[breakpoint].w;
                const verticalPosition = widget.position[breakpoint].y;
                const horizontalPosition = widget.position[breakpoint].x;
                return (
                    <Box
                        id={`widget-${widget.key}`}
                        sx={getWidgetDivStyle(widget)}
                        key={getWidgetKey(widget, breakpoint)}
                        data-grid={{
                            // THIS breaks when the breakpoint does not exist!
                            x: horizontalPosition,
                            y: verticalPosition,
                            w: width,
                            h: height,
                            ...getPositionConstraintsFromType(
                                widget,
                                pageSeparators,
                                isFullWidth,
                            ),
                        }}
                    >
                        {editMode && isWidgetTruncated(widget) && (
                            <WidgetTruncatedWarningIcon />
                        )}
                        <WidgetPositionContextProvider
                            value={{
                                currentBreakpoint: breakpoint,
                                md: {
                                    x: widget.position.md.x || 0,
                                    y: widget.position.md.y || 0,
                                    h: widget.position.md.h,
                                    w: widget.position.md.w,
                                },
                                xs: {
                                    x: widget.position.xs.x || 0,
                                    y: widget.position.xs.y || 0,
                                    h: widget.position.xs.h,
                                    w: widget.position.xs.w,
                                },
                                userInitialYPosition,
                            }}
                        >
                            <Widget widget={widget} />
                        </WidgetPositionContextProvider>
                    </Box>
                );
            })}
        </ResponsiveGridLayout>
    );
};
